import React, { useState, useEffect, forwardRef } from 'react';
import { api } from '@/api';

/**
 * PrintableDocument - Wrapper component that adds company header/footer to printable documents
 * 
 * Usage:
 * <PrintableDocument 
 *   title="Notice of Delay"
 *   reference="NOT-001"
 *   date="2024-02-22"
 *   projectName="Test Project"
 * >
 *   {document content}
 * </PrintableDocument>
 */
const PrintableDocument = forwardRef(({ 
  children, 
  title = "Document",
  reference = "",
  date = "",
  projectName = "",
  projectNumber = "",
  recipientName = "",
  recipientCompany = "",
  recipientAddress = "",
  showSignature = true,
  customFooter = null,
  className = ""
}, ref) => {
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const res = await api.companySettings.getDocumentHeader();
      setCompanyInfo(res.data);
    } catch (error) {
      console.error('Failed to load company info:', error);
      setCompanyInfo({
        company_name: '[Configure Company Settings]',
        logo_url: null,
        address: '[Address not configured]',
        contact: '[Contact not configured]',
        signatory_name: '[Signatory Name]',
        signatory_position: '[Position]'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-ZA', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-400">Loading document...</div>;
  }

  return (
    <div 
      ref={ref}
      className={`bg-white print:bg-white ${className}`}
      style={{ 
        fontFamily: "'Segoe UI', 'Arial', sans-serif",
        fontSize: '11pt',
        lineHeight: '1.5'
      }}
    >
      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 15mm 20mm 20mm 20mm;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .no-print {
              display: none !important;
            }
            .print-break-before {
              page-break-before: always;
            }
            .print-break-after {
              page-break-after: always;
            }
            .print-avoid-break {
              page-break-inside: avoid;
            }
          }
        `}
      </style>

      {/* Document Header with Logo */}
      <div className="border-b-2 border-slate-300 pb-4 mb-6">
        <div className="flex justify-between items-start">
          {/* Logo and Company Name */}
          <div className="flex items-start gap-4">
            {companyInfo?.logo_url ? (
              <img 
                src={companyInfo.logo_url} 
                alt="Company Logo" 
                className="h-16 w-auto object-contain"
                style={{ maxWidth: '180px' }}
              />
            ) : (
              <div className="h-16 w-16 bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-xs">
                LOGO
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-slate-900">{companyInfo?.company_name}</h1>
              {companyInfo?.trading_name && (
                <p className="text-sm text-slate-600">t/a {companyInfo.trading_name}</p>
              )}
              {companyInfo?.registration && (
                <p className="text-xs text-slate-500">Reg: {companyInfo.registration}</p>
              )}
            </div>
          </div>
          
          {/* Company Contact Info */}
          <div className="text-right text-sm text-slate-600">
            <div className="whitespace-pre-line">{companyInfo?.address}</div>
            <div className="mt-1 text-xs">{companyInfo?.contact}</div>
          </div>
        </div>
      </div>

      {/* Document Title and Reference */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold uppercase tracking-wide text-slate-900">{title}</h2>
            {reference && (
              <p className="text-sm text-slate-600 mt-1">Reference: <span className="font-mono font-medium">{reference}</span></p>
            )}
          </div>
          <div className="text-right text-sm">
            <p className="text-slate-600">Date: <span className="font-medium">{formatDate(date)}</span></p>
            {projectName && (
              <p className="text-slate-600 mt-1">Project: <span className="font-medium">{projectName}</span></p>
            )}
            {projectNumber && (
              <p className="text-slate-500 text-xs">Project No: {projectNumber}</p>
            )}
          </div>
        </div>
      </div>

      {/* Recipient Info (if provided) */}
      {(recipientName || recipientCompany) && (
        <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded">
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">To:</p>
          {recipientCompany && <p className="font-medium">{recipientCompany}</p>}
          {recipientName && <p className="text-slate-700">{recipientName}</p>}
          {recipientAddress && <p className="text-sm text-slate-600 whitespace-pre-line">{recipientAddress}</p>}
        </div>
      )}

      {/* Document Content */}
      <div className="document-content mb-8">
        {children}
      </div>

      {/* Signature Block */}
      {showSignature && (
        <div className="mt-12 print-avoid-break">
          <p className="text-sm text-slate-600 mb-8">Yours faithfully,</p>
          <div className="border-t border-slate-300 w-48 pt-2">
            <p className="font-medium">{companyInfo?.signatory_name}</p>
            <p className="text-sm text-slate-600">{companyInfo?.signatory_position}</p>
            <p className="text-sm text-slate-500">{companyInfo?.company_name}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-center text-slate-500">
        {customFooter || companyInfo?.document_footer || (
          <span>
            {companyInfo?.company_name}
            {companyInfo?.registration && ` | Reg: ${companyInfo.registration}`}
            {companyInfo?.vat_number && ` | VAT: ${companyInfo.vat_number}`}
          </span>
        )}
      </div>
    </div>
  );
});

PrintableDocument.displayName = 'PrintableDocument';

export default PrintableDocument;


/**
 * PrintButton - Utility component for triggering print
 */
export function PrintButton({ targetRef, className = "", children }) {
  const handlePrint = () => {
    if (targetRef?.current) {
      const printContents = targetRef.current.innerHTML;
      const originalContents = document.body.innerHTML;
      
      document.body.innerHTML = `
        <html>
          <head>
            <title>Print Document</title>
            <style>
              @media print {
                @page { size: A4; margin: 15mm 20mm; }
                body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11pt; line-height: 1.5; }
              }
              body { padding: 20px; }
            </style>
          </head>
          <body>${printContents}</body>
        </html>
      `;
      
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    } else {
      window.print();
    }
  };

  return (
    <button onClick={handlePrint} className={className}>
      {children || 'Print'}
    </button>
  );
}


/**
 * useDocumentHeader - Hook to get company header info
 */
export function useDocumentHeader() {
  const [headerInfo, setHeaderInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHeader = async () => {
      try {
        const res = await api.companySettings.getDocumentHeader();
        setHeaderInfo(res.data);
      } catch (error) {
        console.error('Failed to load document header:', error);
      } finally {
        setLoading(false);
      }
    };
    loadHeader();
  }, []);

  return { headerInfo, loading };
}
